import { z } from 'zod'

import { TripId } from '@/types/common'
import { LocationId } from '@/types/location'
import {
	RailDiscountCardZ,
	RailPassengerTypeZ,
	RailReservationOfferId,
	RailReservationOfferIdZ,
} from '@/types/rails/common'
import {
	RailCancellationOfferResultZ,
	RailSearchExtendPageRequest,
	RailSearchExtendPageRequestZ,
	RailSearchSetInwardRequest,
	RailSearchSetInwardRequestZ,
} from '@/types/rails/search'
import {
	CancelSelectedSeatsRequestZ,
	GetRailSeatMapsJobResultResultZ,
	RailSeatSelection,
	UpdateSelectedSeatsRequestZ,
} from '@/types/rails/seatMap'
import { RailSelectedSeatPreference, RailSelectedSeatPreferenceRequestZ } from '@/types/rails/seatPreference'
import { SearchId } from '@/types/search'
import { BookingSegmentId, BookingSegmentIdZ } from '@/types/trip'

import { getGGClient, RequestOverrides } from './GetGoingClient'

export async function setRailSearchInward(
	searchId: SearchId,
	payload: RailSearchSetInwardRequest,
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.request('POST', `rails/search/${searchId}/search_inward`, {
			requestSchema: RailSearchSetInwardRequestZ,
			data: payload,
			...overrides,
		})
		.then((r) => r.data)
}

export async function extendRailSearchPage(
	searchId: SearchId,
	payload: RailSearchExtendPageRequest,
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.request('POST', `rails/search/${searchId}/paginate`, {
			requestSchema: RailSearchExtendPageRequestZ,
			data: payload,
			...overrides,
		})
		.then((r) => r.data)
}

export async function getSeatMaps(bookingSegmentId: number, overrides?: RequestOverrides) {
	return getGGClient().simplePollingRequest(`rails/${bookingSegmentId}/seat_map`, {
		resultSchema: GetRailSeatMapsJobResultResultZ,
		...overrides,
	})
}

getSeatMaps.queryKey = (bookingSegmentId: number) => ['rail', bookingSegmentId, 'seatMap'] as const

export async function cancelSelectedSeats(
	bookingSegmentId: BookingSegmentId,
	seats: RailSeatSelection[],
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.request('DELETE', `rails/${bookingSegmentId}/seats`, {
			requestSchema: CancelSelectedSeatsRequestZ,
			data: {
				seats,
			},
			...overrides,
		})
		.then((r) => r.data)
}

export async function updateSelectedSeats(
	bookingSegmentId: BookingSegmentId,
	seats: RailSeatSelection[],
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.request('PUT', `rails/${bookingSegmentId}/seats`, {
			requestSchema: UpdateSelectedSeatsRequestZ,
			data: {
				seats,
			},
			...overrides,
		})
		.then((r) => r.data)
}

export async function getRailCancellationOffer(booking_segments_ids: BookingSegmentId[], overrides?: RequestOverrides) {
	return getGGClient().simplePollingRequest(`rails/cancellation_offer`, {
		requestSchema: z.object({
			booking_segments_ids: BookingSegmentIdZ.array(),
		}),
		resultSchema: RailCancellationOfferResultZ,
		data: { booking_segments_ids },
		...overrides,
	})
}

getRailCancellationOffer.queryKey = (sortedBsttIds: BookingSegmentId[]) =>
	['rails', 'cancellation_offer', ...sortedBsttIds] as const

export async function cancelRailSegments(
	tripId: TripId,
	booking_segment_ids: BookingSegmentId[],
	reason: string,
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.request('POST', `trips/${tripId}/cancel_trains`, {
			requestSchema: z.object({
				booking_segment_ids: BookingSegmentIdZ.array(),
				reason: z.string(),
			}),
			data: {
				booking_segment_ids,
				reason,
			},
			...overrides,
		})
		.then((r) => r.data)
}

export async function cancelRailSegmentsLegacy(
	tripId: TripId,
	booking_segments_ids: BookingSegmentId[],
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.request('POST', `trips/${tripId}/cancel_train`, {
			requestSchema: z.object({
				booking_segments_ids: BookingSegmentIdZ.array(),
			}),
			data: {
				booking_segments_ids,
			},
			...overrides,
		})
		.then((r) => r.data)
}

export async function getTicketDiscounts(enableNewSearch: boolean, overrides?: RequestOverrides) {
	return getGGClient()
		.request('GET', 'rail/rail_cards', {
			params: enableNewSearch ? { new_rail_search: true } : undefined,
			responseSchema: RailDiscountCardZ.array(),
			...overrides,
		})
		.then((r) => r.data)
}

getTicketDiscounts.queryKey = (enableNewSearch: boolean) => ['rail', 'rail_cards', enableNewSearch]

export async function getPassengerTypes(
	fromLocationId: LocationId,
	toLocationId: LocationId,
	overrides?: RequestOverrides,
) {
	return getGGClient().simplePollingRequest(`rails/get_passenger_types`, {
		requestSchema: z.object({
			origin: z.string().or(z.number()),
			destination: z.string().or(z.number()),
		}),
		resultSchema: z.object({ passenger_types: RailPassengerTypeZ.array() }),
		data: { origin: fromLocationId, destination: toLocationId },
		...overrides,
	})
}

getPassengerTypes.queryKey = (fromLocationId: LocationId, toLocationId: LocationId) =>
	['rail', 'get_passenger_types', fromLocationId, toLocationId] as const

export async function saveSeatPreferences(
	bookingSegmentId: BookingSegmentId,
	seatPreferences: RailSelectedSeatPreference[],
	overrides?: RequestOverrides,
) {
	await getGGClient().request('PUT', `rails/${bookingSegmentId}/seat_preferences`, {
		requestSchema: RailSelectedSeatPreferenceRequestZ,
		data: { seat_preferences: seatPreferences },
		...overrides,
	})
}

export async function selectReservationOffers(
	bookingSegmentId: BookingSegmentId,
	selectedResOfferIds: RailReservationOfferId[],
	/** For ATOC we need to explicitly click on "Don't reserve" */
	unselectedResOfferIds: RailReservationOfferId[],
	overrides?: RequestOverrides,
) {
	await getGGClient().request('PUT', `rails/${bookingSegmentId}/reservation_offers`, {
		requestSchema: z.strictObject({
			selected_ids: RailReservationOfferIdZ.array(),
			unselected_ids: RailReservationOfferIdZ.array(),
		}),
		data: {
			selected_ids: selectedResOfferIds,
			unselected_ids: unselectedResOfferIds,
		},
		...overrides,
	})
}

export async function setSeenSeatPreferencesFlag(
	bookingSegmentId: BookingSegmentId,
	value: boolean,
	overrides?: RequestOverrides,
) {
	await getGGClient().request('PATCH', `rails/${bookingSegmentId}/seen_seat_preferences`, {
		requestSchema: z.strictObject({
			seen_seat_preferences: z.boolean(),
		}),
		data: {
			seen_seat_preferences: value,
		},
		...overrides,
	})
}
